import {
  Box,
  BoxProps,
  Flex,
  Icon,
  ImageProps,
  Link,
  Text,
  useBreakpointValue
} from '@chakra-ui/react';
import { ReactComponent as PlusYellowIcon } from 'assets_2/icons/plus-text-yellow-icon.svg';
import BadgeContent from 'components/BadgeContent/BadgeContent';
import ImageWithBlurHash from 'components/ImageLoader/ImageWithBlurHash';
import RatingStar from 'components/RatingStar/RatingStar';
import { getDurationInMinutes } from 'helpers/datetime';
import { getValidBaseLocale } from 'locales/helpers';
import isNumber from 'lodash/isNumber';
import React from 'react';
import { useLocation, useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { getLibraryItemImageUrl } from 'services/media';
import { LibraryItem } from 'services/singles';
import { ReactComponent as CourseIcon } from '../../Assets/images/icons/course-book.svg';
import { ReactComponent as HeartIcon } from '../../Assets/images/icons/heartIcon.svg';
import { ReactComponent as MusicIcon } from '../../assets_2/icons/category/music.svg';
import { ReactComponent as TalksIcon } from '../../assets_2/icons/category/talk.svg';
import getLibraryItemLink from './getLibraryItemLink';

export interface LibraryItemCardProps extends BoxProps {
  libraryItem: LibraryItem;
  showHeartIcon?: boolean;
  imageProps?: ImageProps;
  imageCardProps?: BoxProps;
  imageSize?: 'small' | 'medium' | 'large' | 'xlarge';
  imageBlurHashContainerProps?: BoxProps;
  openInNewTab?: boolean;
  isHideImage?: boolean;
}

type RouteParams = {
  lang: string;
};

const LibraryItemCard = ({
  libraryItem,
  showHeartIcon,
  imageProps,
  imageSize,
  imageBlurHashContainerProps = {},
  imageCardProps = {},
  openInNewTab,
  isHideImage,
  ...containerProps
}: LibraryItemCardProps) => {
  const {
    id,
    title,
    media_length,
    publisher,
    content_type,
    rating_score,
    slug,
    blurhash,
    item_type,
    days,
    is_premium
  } = libraryItem;

  const libraryImageProps = imageProps ?? {};
  const { lang } = useParams<RouteParams>();
  const gmLang = getValidBaseLocale(lang) ?? undefined;
  const libraryItemLink = getLibraryItemLink({
    libraryItemId: id,
    itemType: item_type,
    slug,
    publisherName: publisher?.username,
    lang: gmLang
  });

  const defaultImageSize = useBreakpointValue({
    base: 'small',
    md: 'medium'
  }) as 'medium' | 'small';

  const image = getLibraryItemImageUrl(
    id,
    'square',
    imageSize || defaultImageSize
  );

  const { pathname } = useLocation();

  return (
    <Link
      as={RouterLink}
      target={openInNewTab ? '_blank' : undefined}
      to={{
        pathname: libraryItemLink,
        state: {
          from: pathname
        }
      }}
      textDecoration="none !important"
      _focus={{
        outline: 'none'
      }}
      _focusVisible={{
        '& .track-image-container': {
          outline: '2px solid black !important'
        }
      }}
    >
      <Box {...containerProps}>
        <Box
          position="relative"
          cursor="pointer"
          borderRadius={10}
          transition="0.3s"
          className="track-image-container"
          _hover={{ boxShadow: 'xl', transform: 'translate(0, -7px)' }}
          {...imageCardProps}
        >
          {showHeartIcon && (
            <Flex position="absolute" right="12px" top="13px">
              <HeartIcon width="16px" height="14px" />
            </Flex>
          )}
          {!isHideImage && (
            <ImageWithBlurHash
              src={image}
              borderRadius={10}
              height="200px"
              width="100%"
              objectFit="cover"
              bgColor="neutral.200"
              blurhash={blurhash}
              containerProps={imageBlurHashContainerProps}
              alt=""
              {...libraryImageProps}
            />
          )}
          <Box position="absolute" left="8px" bottom="8px">
            <BadgeContent
              py="3px"
              bgColor={
                item_type === 'COURSES' ? '#D9A853' : 'rgba(30, 33, 33, 0.6)'
              }
              textContent={
                <Text
                  fontWeight={400}
                  color="white"
                  fontSize="11px"
                  lineHeight="15px"
                  textTransform="uppercase"
                >
                  {item_type === 'COURSES'
                    ? 'COURSES'
                    : getDurationInMinutes(media_length)}
                </Text>
              }
              imageContent={
                <>
                  {item_type !== 'COURSES' && content_type === 'MUSIC' && (
                    <MusicIcon
                      fill="var(--neutral-0)"
                      width="9px"
                      height="10px"
                    />
                  )}
                  {item_type !== 'COURSES' && content_type === 'TALKS' && (
                    <TalksIcon
                      fill="var(--neutral-0)"
                      width="9px"
                      height="11px"
                    />
                  )}
                  {item_type === 'COURSES' && (
                    <CourseIcon
                      width="9px"
                      height="9px"
                      fill="var(--neutral-0)"
                    />
                  )}
                </>
              }
            />
          </Box>
        </Box>
        <Box my="14px">
          <Flex align="center" color="neutral.600">
            {isNumber(rating_score) && (
              <RatingStar
                color={
                  rating_score > 4.8
                    ? 'var(--yellow-400)'
                    : 'var(--neutral-600)'
                }
                ratingScore={rating_score}
              />
            )}
            <Text
              fontSize="11px"
              textTransform="uppercase"
              lineHeight="15px"
              fontWeight={700}
              color="neutral.600"
            >
              {isNumber(rating_score) && `• `}
              {item_type === 'COURSES' ? `${days} DAYS` : content_type}
            </Text>

            {is_premium && (
              <>
                <Text
                  fontSize="11px"
                  lineHeight="15px"
                  fontWeight={700}
                  textTransform="uppercase"
                  color="neutral.600"
                  whiteSpace="pre-wrap"
                >
                  {` • `}
                </Text>
                <Icon as={PlusYellowIcon} width="28px" height="16px" />
              </>
            )}
          </Flex>
          <Box height="6px" />
          <Text
            fontSize="18px"
            lineHeight="23px"
            fontWeight={500}
            color="neutral.700"
            noOfLines={2}
          >
            {title}
          </Text>
          <Box height="4px" />
          <Text
            fontSize="14px"
            lineHeight="19px"
            fontWeight={500}
            color="neutral.500"
          >
            {publisher.name}
          </Text>
        </Box>
      </Box>
    </Link>
  );
};

export default LibraryItemCard;
